// CryptoPayPortalDirect.js
// Direct payment portal — plan config driven by paymentLinkData prop (from URL params).

import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { NETWORKS } from '../config/networks';
import { connectWallet as connectExternalWallet, getProvider as getWalletProvider, getConnectedAccounts, onAccountsChanged } from '../lib/wallet';

const SUBS_ABI = [
  "function createSubscription(address merchant, uint256 amountPerCycle, uint256 interval, string planName) returns (bytes32)",
  "event SubscriptionCreated(bytes32 indexed subscriptionId, address indexed customer, address indexed merchant, uint256 amountPerCycle, uint256 interval, string planName)"
];

const ERC20_ABI = [
  "function approve(address spender, uint256 amount) returns (bool)",
  "function balanceOf(address) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)"
];

const CHAIN_ID_TO_USDC = Object.fromEntries(
  Object.values(NETWORKS).map(n => [n.chainId, n.usdcAddress.toLowerCase()])
);
const CHAIN_ID_TO_RPC = Object.fromEntries(
  Object.values(NETWORKS).map(n => [n.chainId, n.rpcUrl])
);

const GAS_TOKEN = {
  84532: 'Sepolia ETH',
};

const FAUCET_URL = {
  84532: 'https://sepoliafaucet.com',
};

const INTERVAL_LABEL = {
  [30  * 86400]: 'month',
  [90  * 86400]: 'quarter',
  [365 * 86400]: 'year',
};

const CHAIN_TO_NETWORK_ID = {
  8453:  'base',
  84532: 'baseSepolia',
};

const DEFAULT_FEATURES = [
  'Unlimited access to all issues',
  'Weekly newsletter delivered to your inbox',
  'Exclusive subscriber-only content',
  'Full archive access',
  'Early access to new features',
  'Cancel anytime',
];

const fmtAmt = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function parseError(err) {
  if (err.message?.includes('user rejected') || err.message?.includes('User denied')) return 'Transaction cancelled';
  if (err.message?.includes('insufficient funds')) return 'Insufficient ETH for gas fees';
  if (err.message?.includes('ERC20: insufficient allowance')) return 'Insufficient allowance';
  return 'Transaction failed';
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full">{children}</div>
    </div>
  );
}

function CheckIcon({ className }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ConnectStep({ planName, planFeatures, displayPrice, tokenSymbol, intervalLabel, merchantName, loading, error, onConnect }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
      <div className="px-6 pt-6 pb-4">
        <h2 className="text-lg font-semibold text-navy-900">{planName}</h2>
        <div className="flex items-baseline gap-1.5 mt-2">
          <span className="text-4xl font-bold text-navy-900">${fmtAmt(displayPrice)}</span>
          <span className="text-base text-navy-500">{tokenSymbol} / {intervalLabel}</span>
        </div>
      </div>

      <div className="px-6 pb-5">
        {planFeatures.map((feature, i) => (
          <div key={i} className="flex items-center gap-3 py-2">
            <CheckIcon className="text-emerald-500 flex-shrink-0" />
            <span className="text-sm text-navy-700">{feature}</span>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-100 mx-6"></div>

      <div className="px-6 py-5">
        <p className="text-sm text-navy-500 mb-4">Connect your wallet to subscribe</p>
        <button
          onClick={onConnect}
          disabled={loading}
          className="w-full bg-slate-800 hover:bg-slate-900 disabled:bg-slate-400 text-white font-medium py-3.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-base"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Connecting...
            </>
          ) : 'Connect Wallet'}
        </button>
        {error && <p className="mt-4 text-sm text-red-500 text-center">{error}</p>}
      </div>
    </div>
  );
}

function ReviewStep({
  planName, planFeatures, annualDiscount, hasAnnualOption, tokenSymbol,
  isAnnual, displayPrice, annualPerMonth, intervalLabel,
  walletAddress, usdcBalance, error, loading, setBillingCycle, onSubscribe,
  subscriberEmail, setSubscriberEmail
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
      <div className="px-6 pt-6 pb-4">
        <h2 className="text-lg font-semibold text-navy-900">{planName}</h2>

        {hasAnnualOption && (
          <div className="flex items-center gap-1 mt-3 bg-slate-100 rounded-lg p-0.5 w-fit">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                !isAnnual ? 'bg-white text-navy-900 shadow-sm' : 'text-navy-500 hover:text-navy-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                isAnnual ? 'bg-white text-navy-900 shadow-sm' : 'text-navy-500 hover:text-navy-700'
              }`}
            >
              Annual
              <span className="ml-1 text-emerald-600">-{annualDiscount}%</span>
            </button>
          </div>
        )}

        <div className="flex items-baseline gap-1.5 mt-2">
          <span className="text-4xl font-bold text-navy-900">${fmtAmt(displayPrice)}</span>
          <span className="text-base text-navy-500">{tokenSymbol} / {intervalLabel}</span>
        </div>
        {isAnnual && (
          <p className="text-xs text-emerald-600 font-medium mt-1">
            ${fmtAmt(annualPerMonth)}/mo billed annually
          </p>
        )}
      </div>

      <div className="px-6 pb-5">
        {planFeatures.map((feature, i) => (
          <div key={i} className="flex items-center gap-3 py-2">
            <CheckIcon className="text-emerald-500 flex-shrink-0" />
            <span className="text-sm text-navy-700">{feature}</span>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-100 mx-6"></div>

      <div className="px-6 py-4">
        <div className="flex justify-between items-center py-1.5">
          <span className="text-sm text-navy-500">Wallet</span>
          <span className="text-sm text-navy-800 font-mono">{walletAddress?.slice(0, 10)}...{walletAddress?.slice(-8)}</span>
        </div>
        <div className="flex justify-between items-center py-1.5">
          <span className="text-sm text-navy-500">Balance</span>
          <span className="text-sm text-navy-800 font-semibold">${fmtAmt(usdcBalance)} {tokenSymbol}</span>
        </div>
      </div>

      <div className="px-6 pb-4">
        <label className="block text-xs font-medium text-navy-500 mb-1.5">
          Email <span className="font-normal text-navy-400">(optional — for content access notifications)</span>
        </label>
        <input
          type="email"
          value={subscriberEmail}
          onChange={(e) => setSubscriberEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-navy-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 transition-colors"
        />
      </div>

      <div className="px-6 pb-6">
        <button
          onClick={onSubscribe}
          disabled={loading}
          className="w-full bg-slate-800 hover:bg-slate-900 disabled:bg-slate-400 text-white font-medium py-3.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-base"
        >
          Subscribe
        </button>
        {error && <p className="mt-3 text-sm text-red-500 text-center">{error}</p>}
        <p className="text-xs text-navy-500 text-center mt-4 leading-relaxed">
          Auto-renews {isAnnual ? 'annually' : `every ${intervalLabel}`}. Cancel anytime.
        </p>
      </div>
    </div>
  );
}

function ProcessingStep({ step }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
      <div className="w-10 h-10 border-2 border-slate-800 border-t-transparent rounded-full animate-spin mx-auto mb-5"></div>
      <h2 className="text-lg font-semibold text-navy-900 mb-1">
        {step === 'approving' ? 'Approving USDC' : 'Creating Subscription'}
      </h2>
      <p className="text-sm text-navy-500">
        {step === 'approving' ? 'Confirm the approval in your wallet' : 'Confirm the transaction in your wallet'}
      </p>
    </div>
  );
}

function SuccessStep({ planName, tokenSymbol, displayPrice, intervalLabel, isAnnual, subscriptionId, networkId }) {
  const rows = [
    ['Plan', planName, false],
    ['Amount', `$${fmtAmt(displayPrice)} ${tokenSymbol} / ${intervalLabel}`, false],
    ['Billing', `${isAnnual ? 'Annual' : intervalLabel.charAt(0).toUpperCase() + intervalLabel.slice(1) + 'ly'}, auto-renew`, false],
    ...(subscriptionId ? [['Sub ID', `${subscriptionId.slice(0, 10)}...${subscriptionId.slice(-8)}`, true]] : [])
  ];

  const manageHref = subscriptionId && networkId
    ? `/#manage?subscription=${subscriptionId}&network=${networkId}`
    : `/#manage${networkId ? `?network=${networkId}` : ''}`;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
      <div className="px-6 pt-6 pb-4">
        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-navy-900">Subscription Active</h2>
        <p className="text-sm text-navy-500 mt-1">Your {planName} is now active</p>
      </div>

      <div className="px-6 pb-5">
        {rows.map(([label, value, mono]) => (
          <div key={label} className="flex justify-between items-center py-2">
            <span className="text-sm font-medium text-navy-600">{label}</span>
            <span className={`text-sm text-navy-800${mono ? ' font-mono' : ''}`}>{value}</span>
          </div>
        ))}
      </div>

      <div className="px-6 pb-6 border-t border-slate-100 pt-4 flex flex-col items-center gap-3">
        <p className="text-xs text-navy-500 text-center leading-relaxed">
          Payments process automatically every {intervalLabel}. Bookmark the link below to manage or cancel anytime.
        </p>
        <a
          href={manageHref}
          className="text-sm font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2"
        >
          Manage Subscription →
        </a>
      </div>
    </div>
  );
}

export default function CryptoPayPortalDirect({
  subsAddress,
  merchantAddress,
  usdcAddress,
  chainId,
  merchantName = "Our Service",
  onSubscriptionCreated = null,
  paymentLinkData = null
}) {
  // Resolve plan config from paymentLinkData; fall back to defaults.
  const planName        = paymentLinkData?.plan     || 'Premium';
  const planMonthly     = parseFloat(paymentLinkData?.price)    || 100;
  const annualDiscount  = parseFloat(paymentLinkData?.discount) || 0;
  const tokenSymbol     = paymentLinkData?.token    || 'USDC';
  const baseIntervalSec = paymentLinkData?.interval ? parseInt(paymentLinkData.interval) : 30 * 86400;
  const planFeatures    = paymentLinkData?.features
    ? paymentLinkData.features.split('|').map(f => f.trim()).filter(Boolean)
    : DEFAULT_FEATURES;

  // Annual toggle is only shown when base interval is monthly and a discount is set.
  const isMonthlyBase   = baseIntervalSec === 30 * 86400;
  const hasAnnualOption = isMonthlyBase && annualDiscount > 0;

  const annualPrice    = parseFloat((planMonthly * 12 * (1 - annualDiscount / 100)).toFixed(2));
  const annualPerMonth = parseFloat((annualPrice / 12).toFixed(2));

  const [tokenValid, setTokenValid] = useState(null);
  const [tokenError, setTokenError] = useState(null);

  const [step, setStep] = useState('connect');
  const [loading, setLoading] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [error, setError] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [usdcBalance, setUsdcBalance] = useState('0');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const providerRef = useRef(null);

  const isAnnual             = hasAnnualOption && billingCycle === 'annual';
  const displayPrice         = isAnnual ? annualPrice : planMonthly;
  const effectiveIntervalSec = isAnnual ? 365 * 86400 : baseIntervalSec;
  const intervalLabel        = INTERVAL_LABEL[effectiveIntervalSec] || 'cycle';
  const networkId            = CHAIN_TO_NETWORK_ID[chainId] ?? String(chainId);

  useEffect(() => {
    async function validateToken() {
      const canonical = CHAIN_ID_TO_USDC[chainId];
      if (!canonical) {
        setTokenValid(true);
        return;
      }
      if (!usdcAddress || usdcAddress.toLowerCase() !== canonical) {
        setTokenError('Token address does not match the canonical USDC address for this network.');
        setTokenValid(false);
        return;
      }
      try {
        const provider = new ethers.JsonRpcProvider(CHAIN_ID_TO_RPC[chainId]);
        const token = new ethers.Contract(usdcAddress, ERC20_ABI, provider);
        const [symbol, decimals] = await Promise.all([token.symbol(), token.decimals()]);
        if (symbol !== 'USDC' || Number(decimals) !== 6) {
          setTokenError(`Token failed verification: expected USDC with 6 decimals, got ${symbol} with ${decimals}.`);
          setTokenValid(false);
          return;
        }
      } catch {
        // RPC failure — address check passed, allow through
      }
      setTokenValid(true);
    }
    validateToken();
  }, [chainId, usdcAddress]);

  useEffect(() => {
    async function checkWallet() {
      const accounts = await getConnectedAccounts();
      if (accounts.length > 0) { setWalletAddress(accounts[0]); setStep('review'); }
    }
    checkWallet();
    const handleAccountsChanged = (accounts) => {
      if (accounts.length > 0) { setWalletAddress(accounts[0]); setStep('review'); }
      else { setWalletAddress(null); setStep('connect'); }
    };
    return onAccountsChanged(handleAccountsChanged);
  }, []);

  useEffect(() => {
    if (!walletAddress || !usdcAddress || !ethers.isAddress(usdcAddress)) return;
    async function fetchBalance() {
      try {
        if (!providerRef.current) providerRef.current = new ethers.BrowserProvider(getWalletProvider());
        const usdc = new ethers.Contract(usdcAddress, ERC20_ABI, providerRef.current);
        setUsdcBalance(ethers.formatUnits(await usdc.balanceOf(walletAddress), 6));
      } catch (err) {}
    }
    fetchBalance();
  }, [walletAddress, usdcAddress]);

  async function connectWallet() {
    try {
      setLoading(true);
      const provider = await connectExternalWallet();
      const accounts = await provider.request({ method: 'eth_accounts' });
      setWalletAddress(accounts[0]);
      setStep('review');
      setError(null);
    } catch (err) {
      setError(err.message || 'Connection rejected');
    } finally {
      setLoading(false);
    }
  }

  async function approveAndSubscribe() {
    try {
      setLoading(true);
      setError(null);

      if (!ethers.isAddress(subsAddress) || !ethers.isAddress(usdcAddress) || !ethers.isAddress(merchantAddress)) {
        setError('Invalid contract configuration. Please contact support.');
        return;
      }

      const walletProvider = getWalletProvider();
      if (chainId) {
        try {
          await walletProvider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x' + chainId.toString(16) }]
          });
        } catch (e) {
          setError(e.code === 4902 ? 'Add this network to your wallet first.' : 'Please switch to the correct network in your wallet.');
          return;
        }
      }

      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();

      const gasBalance = await provider.getBalance(walletAddress);
      if (gasBalance < ethers.parseEther('0.001')) {
        const token = GAS_TOKEN[chainId] || 'ETH';
        const faucet = FAUCET_URL[chainId];
        setError(`Insufficient ${token} for gas fees.${faucet ? ` Get some at ${faucet}` : ''}`);
        return;
      }

      const amount = ethers.parseUnits(displayPrice.toFixed(6), 6);
      const usdc = new ethers.Contract(usdcAddress, ERC20_ABI, signer);

      const usdcBal = await usdc.balanceOf(walletAddress);
      if (usdcBal < amount) {
        setError(`Insufficient USDC balance. You need at least $${fmtAmt(displayPrice)} USDC to subscribe.`);
        return;
      }

      setStep('approving');
      const approvalAmount = amount * 120n; // 10 years of cycles — avoids unlimited-approval flag
      const currentAllowance = await usdc.allowance(walletAddress, subsAddress);
      if (currentAllowance < amount) {
        await (await usdc.approve(subsAddress, approvalAmount, { gasLimit: 100000 })).wait();
      }

      setStep('subscribing');
      const subs = new ethers.Contract(subsAddress, SUBS_ABI, signer);
      const receipt = await (await subs.createSubscription(merchantAddress, amount, effectiveIntervalSec, planName, { gasLimit: 300000 })).wait();

      let newSubId = null;
      for (const log of receipt.logs) {
        try {
          const parsed = subs.interface.parseLog(log);
          if (parsed?.name === 'SubscriptionCreated') { newSubId = parsed.args.subscriptionId; break; }
        } catch { continue; }
      }

      setSubscriptionId(newSubId);

      // Store subscriber email off-chain (non-blocking, non-fatal)
      if (subscriberEmail && newSubId) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
        fetch(`${apiUrl}/api/subscriptions/${newSubId}/email`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: subscriberEmail, network: networkId }),
        }).catch(() => {});
      }

      setStep('success');
      onSubscriptionCreated?.({ subscriptionId: newSubId, customer: walletAddress, planName, amount: displayPrice, interval: billingCycle });
    } catch (err) {
      setError(parseError(err));
      setStep('review');
    } finally {
      setLoading(false);
    }
  }

  const commonProps = { planName, tokenSymbol, displayPrice, intervalLabel, isAnnual };

  const stepContent = {
    connect:     <ConnectStep planName={planName} planFeatures={planFeatures} displayPrice={displayPrice} tokenSymbol={tokenSymbol} intervalLabel={intervalLabel} merchantName={merchantName} loading={loading} error={error} onConnect={connectWallet} />,
    review:      <ReviewStep
                   {...commonProps}
                   planFeatures={planFeatures}
                   annualDiscount={annualDiscount} hasAnnualOption={hasAnnualOption} annualPerMonth={annualPerMonth}
                   walletAddress={walletAddress} usdcBalance={usdcBalance}
                   error={error} loading={loading} setBillingCycle={setBillingCycle} onSubscribe={approveAndSubscribe}
                   subscriberEmail={subscriberEmail} setSubscriberEmail={setSubscriberEmail}
                 />,
    approving:   <ProcessingStep step={step} />,
    subscribing: <ProcessingStep step={step} />,
    success:     <SuccessStep {...commonProps} subscriptionId={subscriptionId} networkId={networkId} />,
  };

  if (tokenValid === null) return <Shell><p className="text-center text-navy-500 py-8">Verifying payment token...</p></Shell>;
  if (tokenValid === false) return <Shell><p className="text-center text-red-600 font-medium py-8">Payment unavailable: {tokenError}</p></Shell>;

  return <Shell>{stepContent[step]}</Shell>;
}
